/**
 * Explicit Writing Town mapping + active-game catalogs.
 * Source of truth: WritingTownCourse.swift and WritingLessonTwo–TwelveCourse.swift.
 *
 * Visible stop N maps to internal writing.lesson(N+1). Do not derive this.
 * familySort exists in lessons 2–4 for legacy decode only and is never required.
 */

export const WRITING_TOWN_KEY = "writing.town";

export const WRITING_TOWN_IDS = [
  "line-lane",
  "curve-corner",
  "dot-park",
  "letter-market",
  "sunny-cafe",
  "pet-shop",
  "garden-signs",
  "story-street",
  "town-hall",
  "my-story-workshop",
  "pow-author-studio",
];

export const WRITING_LEGACY_ACTIVITY_IDS = new Set(["familySort"]);

export const WRITING_TOWN_PALETTE = {
  ink: "#142E47",
  muted: "#546E7A",
  coral: "#FA636E",
  mint: "#33B380",
  mintDeep: "#1A855C",
  sun: "#FFBF29",
  coralDeep: "#CC3B4A",
  lock: "#8CAB9C",
  lockDeep: "#5C7D70",
  cream: "#FFFAE3",
};

const rgb = (r, g, b) =>
  `#${[r, g, b]
    .map((n) =>
      Math.round(n * 255)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;

const game = (id, title, instruction, engine, accent, symbol, extras = {}) => ({
  id,
  title,
  instruction,
  engine,
  accent,
  symbol,
  ...extras,
});

const L2_ACTIVE = [
  game(
    "watchAndTap",
    "Watch and Tap",
    "Watch the path, then tap the sound's letter.",
    "watchAndTap",
    rgb(0.08, 0.48, 0.84),
    "touch",
    { intro: "Watch how Pip makes each lowercase letter. Then tap the letter that matches its sound." },
  ),
  game(
    "arrowTrace",
    "Guided Trace",
    "Trace each lowercase letter twice.",
    "guidedTrace",
    rgb(0.1, 0.65, 0.58),
    "trace",
    { intro: "Follow the faded path and trace each lowercase letter twice.", tracesPerLetter: 2 },
  ),
  game(
    "fromMemory",
    "From Memory",
    "Write each letter three times without a guide.",
    "fromMemory",
    rgb(0.55, 0.32, 0.86),
    "trace",
    { intro: "The guide is hiding now. Write each lowercase letter three times from memory.", writesPerLetter: 3 },
  ),
  game(
    "whichIsRight",
    "Which Is Right?",
    "Find the right letter, then write it.",
    "whichIsRight",
    rgb(0.93, 0.43, 0.29),
    "grid",
    { intro: "One letter is facing the right way. Find it, then write it.", rounds: 5 },
  ),
  game(
    "mysteryLetter",
    "Mystery Letter",
    "Listen to the word clue, then write its first letter.",
    "mysteryLetter",
    rgb(0.91, 0.6, 0.08),
    "listen",
    { intro: "Listen for the word clue, then write its first lowercase letter when you are ready." },
  ),
  game(
    "fastLetters",
    "Fast Letters",
    "Write as many known letters as you can.",
    "fastLetters",
    rgb(0.08, 0.55, 0.76),
    "trace",
    { intro: "Write the lowercase letters you know. Check each one and keep going until the timer ends.", seconds: 60 },
  ),
  game(
    "wordInSentence",
    "Word in a Sentence",
    "Listen to the word and write it in the blank.",
    "wordInSentence",
    rgb(0.83, 0.31, 0.55),
    "words",
    { intro: "A picture is in the sentence. Listen to its sounds, then write the whole lowercase word." },
  ),
];

const L3_ACTIVE = [
  game("watchAndTap", "Watch and Tap", "Watch Pip form it, then tap its sound.", "watchAndTap", rgb(0.11, 0.5, 0.83), "touch", {
    intro: "Pip will paint each bump and tail letter. Watch the path, listen, then tap the matching lowercase letter.",
    paintPanda: true,
  }),
  game("arrowTrace", "Guided Trace", "Follow the faded path and trace each letter twice.", "guidedTrace", rgb(0.37, 0.34, 0.88), "trace", {
    intro: "Follow the faded path. Trace every bump and tail letter twice.",
    tracesPerLetter: 2,
  }),
  game("fromMemory", "From Memory", "Listen and write each letter three times.", "fromMemory", rgb(0.08, 0.48, 0.7), "trace", {
    intro: "The guide is hiding. Listen and write every bump and tail letter three times from memory.",
    writesPerLetter: 3,
  }),
  game("whichIsRight", "Which Is Right?", "Find the right-facing letter, then write it.", "whichIsRight", rgb(0.63, 0.34, 0.82), "grid", {
    intro: "Find the lowercase letter facing the right way. Then write it before the next round.",
    rounds: 5,
  }),
  game("mysteryLetter", "Mystery Letter", "Hear a sound and write its mystery letter.", "mysteryLetter", rgb(0.08, 0.61, 0.49), "listen", {
    intro: "Listen to a known letter sound and write its lowercase letter. The timer gives a gentle hint, never a penalty.",
    timerHint: true,
  }),
  game("fastLetters", "Fast Letters", "Write known letters for one friendly minute.", "fastLetters", rgb(0.93, 0.48, 0.18), "trace", {
    intro: "Write as many known lowercase letters as you can in one minute. Your own last score is the only score to beat.",
    seconds: 60,
    knownPool: true,
  }),
  game("wordInSentence", "Word in a Sentence", "Stretch the sounds and write the picture word.", "wordInSentence", rgb(0.87, 0.32, 0.54), "words", {
    intro: "Listen as the picture word is stretched into sounds. Write the whole lowercase word in the sentence.",
  }),
];

const L4_ACTIVE = [
  game("watchAndTap", "Watch and Tap", "Watch Pip form it, then tap its sound.", "watchAndTap", rgb(0.08, 0.53, 0.78), "touch", {
    intro: "Pip will paint each slant and odd letter. Watch the path, listen, then tap the matching lowercase letter.",
    paintPanda: true,
  }),
  game("arrowTrace", "Guided Trace", "Follow the faded path and trace each letter twice.", "guidedTrace", rgb(0.44, 0.33, 0.86), "trace", {
    intro: "Follow the faded path. Trace every slant and odd lowercase letter twice.",
    tracesPerLetter: 2,
  }),
  game("fromMemory", "From Memory", "Listen and write each letter three times.", "fromMemory", rgb(0.08, 0.58, 0.52), "trace", {
    intro: "The guide is hiding. Listen and write every slant and odd lowercase letter three times from memory.",
    writesPerLetter: 3,
  }),
  game("whichIsRight", "Which Is Right?", "Find the forward letter, then write it.", "whichIsRight", rgb(0.66, 0.34, 0.78), "grid", {
    intro: "Find the lowercase letter facing the right way. Then write it before the next round.",
    rounds: 5,
  }),
  game("mysteryLetter", "Mystery Letter", "Hear a sound and write its mystery letter.", "mysteryLetter", rgb(0.1, 0.58, 0.36), "listen", {
    intro: "Listen to a known letter sound and write its lowercase letter. The timer gives a gentle hint, never a penalty.",
    timerHint: true,
  }),
  game("fastLetters", "Fast Letters", "Write known letters for one friendly minute.", "fastLetters", rgb(0.94, 0.45, 0.16), "trace", {
    intro: "Write as many known lowercase letters as you can in one minute. Your own last score is the only score to beat.",
    seconds: 60,
    knownPool: true,
  }),
  game("wordInSentence", "Word in a Sentence", "Stretch the sounds and write the picture word.", "wordInSentence", rgb(0.87, 0.33, 0.47), "words", {
    intro: "Listen as the picture word is stretched into sounds. Write the whole lowercase word in the sentence.",
  }),
];

const L5_ACTIVE = [
  game("myCapital", "My Capital", "Watch, trace twice, then write your capital three times.", "myCapital", rgb(0.45, 0.31, 0.88), "trace", {
    intro: "This is your capital. Watch every numbered stroke begin at the top, trace it twice, then write it three times from memory.",
  }),
  game("straightLineCapitalsOne", "Straight-Line Capitals I", "Trace each right-angle capital, then write it twice.", "straightLineCapitals", rgb(0.08, 0.55, 0.76), "trace", {
    intro: "These six capitals use straight lines and right angles. Trace each one once, then write it twice from memory.",
    group: "rightAngle",
  }),
  game("straightLineCapitalsTwo", "Straight-Line Capitals II", "Trace each slanted capital, then write it twice.", "straightLineCapitals", rgb(0.93, 0.42, 0.3), "trace", {
    intro: "These nine capitals use straight slanted lines. Trace each one once, then write it twice from memory.",
    group: "slanted",
  }),
  game("capitalFromMemory", "Capital From Memory", "Listen and write eight capitals without a model.", "capitalFromMemory", rgb(0.09, 0.58, 0.47), "listen", {
    intro: "Listen for a capital, then write it on the blank line. Eight calm rounds complete the game.",
  }),
  game("bigAndSmall", "Big and Small", "Match eight capitals to their lowercase partners.", "bigAndSmall", rgb(0.72, 0.36, 0.77), "sort", {
    intro: "Tap a capital, then tap its lowercase partner. Match all eight pairs.",
  }),
  game("fastCapitals", "Fast Capitals", "Write known capitals for one friendly minute.", "fastCapitals", rgb(0.93, 0.57, 0.12), "trace", {
    intro: "Choose and write capitals for one friendly minute. The clock is only a helper. Any number counts.",
    seconds: 60,
  }),
];

const L6_ACTIVE = [
  game("curveCapitalsOne", "Curve Capitals I", "Watch, trace once, then write each curved capital twice.", "curveCapitals", rgb(0.18, 0.52, 0.88), "trace", {
    intro: "Meet seven capitals made with curves. Watch the numbered path, trace once, then write each capital twice from memory.",
    group: "curveOne",
    paintPanda: true,
  }),
  game("curveCapitalsTwo", "Curve Capitals II", "Build each line-and-bump capital, then write it twice.", "curveCapitals", rgb(0.48, 0.35, 0.88), "trace", {
    intro: "These four capitals begin with a straight line and add a bump. Watch, trace once, then write each one twice from memory.",
    group: "curveTwo",
    paintPanda: true,
  }),
  game("capitalFromMemory", "Capital From Memory", "Hear a letter and write its capital with no model.", "capitalFromMemory", rgb(0.08, 0.58, 0.48), "trace", {
    intro: "Listen to eight letter sounds from the whole alphabet. Write each capital on the blank line with no model.",
    lesson: 6,
  }),
  game("pairWriting", "Pair Writing", "Write the capital and lowercase partners together.", "pairWriting", rgb(0.1, 0.57, 0.78), "collect", {
    intro: "Every letter has a capital and lowercase partner. Write both partners in eight rounds.",
  }),
  game("bigOrSmall", "Big or Small?", "Choose the letter size that belongs in each sentence.", "bigOrSmall", rgb(0.7, 0.36, 0.8), "grid", {
    intro: "A sentence starts with a capital. A name starts with a capital too. Choose the letter that belongs.",
  }),
  game("capitalWhereItBelongs", "Capital Where It Belongs", "Write capital I and a name where they belong.", "capitalWhereItBelongs", rgb(0.92, 0.48, 0.15), "words", {
    intro: "Capital I stands tall by itself. Names also start with a capital. Write each one where it belongs.",
  }),
  game("nameStartsBig", "Name Starts Big", "Give a friend or pet name a capital first letter.", "nameStartsBig", rgb(0.89, 0.31, 0.49), "words", {
    intro: "Choose a friend or pet name. Its first letter starts big, both alone and inside a sentence.",
  }),
  game("fixTheStart", "Fix the Start", "Find each small sentence start and rewrite it big.", "fixTheStart", rgb(0.14, 0.6, 0.37), "words", {
    intro: "These earlier sentences forgot their capital starts. Find each first letter and rewrite it as a capital.",
  }),
];

const L7_ACTIVE = [
  game("myProfileName", "My Profile Name", "Trace your name, then write it from memory.", "myProfileName", rgb(0.49, 0.34, 0.84), "trace", {
    intro: "Your profile name belongs to you. Trace its letter paths once, then write it from memory with a capital first letter.",
  }),
  game("meetTheCast", "Meet the Cast", "Tap each friend and watch the name appear sound by sound.", "meetTheCast", rgb(0.1, 0.57, 0.8), "search", {
    intro: "Meet Zippy, Pip, and Benny. Tap each friend to watch the name appear one sound at a time.",
  }),
  game("traceTheCast", "Trace the Cast", "Trace and remember Zippy, Pip, and Benny.", "traceTheCast", rgb(0.12, 0.61, 0.4), "trace", {
    intro: "Trace each character name once over the numbered paths. Then write it once from memory, starting with a capital.",
  }),
  game("nameScramble", "Name Scramble", "Put each name in order, then write it.", "nameScramble", rgb(0.9, 0.49, 0.13), "sort", {
    intro: "Put the character name letters in order. Then write the whole name with its big first letter.",
  }),
  game("whoIsIt", "Who Is It?", "Write the right name under each familiar face.", "whoIsIt", rgb(0.16, 0.57, 0.76), "search", {
    intro: "Look at each familiar face and write the matching name underneath.",
  }),
  game("nameTags", "Name Tags", "Write three names to make their name tags appear.", "nameTags", rgb(0.89, 0.34, 0.48), "collect", {
    intro: "Write each character name. A bright name tag will pop into place when the name matches.",
  }),
  game("bigLetterFirst", "Big Letter First", "Choose and write the big first letter, then the name.", "bigLetterFirst", rgb(0.59, 0.35, 0.82), "grid", {
    intro: "Character names are names, so they start with a capital. Choose it, write it, then write the whole name.",
  }),
  game("characterInSentence", "Character in a Sentence", "Write a character name in each sentence.", "characterInSentence", rgb(0.91, 0.43, 0.19), "words", {
    intro: "A character name keeps its capital inside a sentence. Write the pictured name in each blank.",
  }),
  game("nameSprint", "Name Sprint", "Write one familiar name during a friendly minute.", "nameSprint", rgb(0.08, 0.58, 0.52), "trace", {
    intro: "Choose one character name. Write it carefully as many times as you like during one friendly minute. Any number counts.",
    seconds: 60,
  }),
  game("signMyBook", "Sign My Book", "Sign the cover of My Book with your profile name.", "signMyBook", rgb(0.47, 0.34, 0.8), "trace", {
    intro: "This is My Book, a place for everything you write. Sign the cover with your profile name.",
  }),
];

const L8_ACTIVE = [
  game("arrowNumbers", "Guided Numbers", "Trace every numeral twice, then write it three times from memory.", "arrowNumbers", rgb(0.09, 0.56, 0.82), "trace", {
    intro: "Numbers have writing paths too. Trace each numeral twice over the faded path, then write it three times from memory.",
  }),
  game("countAndWrite", "Count and Write", "Count each friendly group and write how many you see.", "countAndWrite", rgb(0.15, 0.62, 0.36), "collect", {
    intro: "Count every friendly group carefully. Touch each one with your eyes, then write the numeral that tells how many.",
  }),
  game("fastNumbers", "Fast Numbers", "Write 0 through 9 in order, again and again for one minute.", "fastNumbers", rgb(0.18, 0.51, 0.88), "trace", {
    intro: "Write zero through nine in order for one friendly minute. Careful shapes matter more than speed.",
    seconds: 60,
  }),
  game("numberInSentence", "Number in a Sentence", "Put a number into two sentences about you and your world.", "numberInSentence", rgb(0.88, 0.39, 0.48), "words", {
    intro: "Numbers can live inside sentences. Write your age, then count two cats and write two cats.",
  }),
  game("numberWords", "Number Words", "Write each numeral and its number word from one to five.", "numberWords", rgb(0.54, 0.35, 0.84), "words", {
    intro: "A number can have a numeral and a word. Write both partners from one to five. Use Show me whenever you need help.",
  }),
];

const L9_ACTIVE = [
  game("soundToLetter", "Sound to Letter", "Hear a sound and tap its letter. Play ten rounds.", "soundToLetter", rgb(0.12, 0.57, 0.82), "listen", {
    intro: "Listen to one sound. Tap the letter that makes it. Ten calm rounds.",
  }),
  game("whichSound", "Which Sound?", "Hear three sounds and tap the one that matches the letter.", "whichSound", rgb(0.5, 0.34, 0.88), "listen", {
    intro: "Look at the letter. Three sounds will play in order. Tap the matching sound.",
  }),
  game("firstAndLast", "First Sound, Last Sound", "Write six first sounds, then six last sounds.", "firstAndLast", rgb(0.94, 0.39, 0.42), "trace", {
    intro: "Listen to each picture word. Write the first sound for six words, then the last sound for six more.",
  }),
  game("soundLadder", "Sound Ladder", "Build each three-sound word, then write it your way.", "soundLadder", rgb(0.96, 0.61, 0.12), "grid", {
    intro: "Climb the sound ladder. Tap three sounds in order, then write the word your way.",
  }),
  game("changeOneSound", "Change One Sound", "Listen to the new word, change one letter, then write it.", "changeOneSound", rgb(0.1, 0.64, 0.49), "grid", {
    intro: "Listen to the new word. Change one letter, then write your new word.",
  }),
  game("stretchAndWrite", "Stretch & Write", "Pick four pictures, stretch each word, and write what you hear.", "stretchAndWrite", rgb(0.91, 0.38, 0.65), "trace", {
    intro: "Pick four pictures. Stretch each word slowly and write the sounds you hear.",
  }),
  game("wordBuilder", "Word Builder", "Build four words on letter buttons, then write each one.", "wordBuilder", rgb(0.17, 0.51, 0.88), "grid", {
    intro: "Build the word you hear on the letter buttons, then write it by hand.",
  }),
  game("wordWall", "Word Wall", "Write four pattern words, then use one in a sentence.", "wordWall", rgb(0.55, 0.35, 0.84), "words", {
    intro: "Listen to four word patterns and write one word from each. Then use one in a sentence.",
  }),
  game("sentenceChoice", "Your Choice in a Sentence", "Choose two picture words and write three sentences.", "sentenceChoice", rgb(0.94, 0.48, 0.2), "words", {
    intro: "Choose both blank words from pictures. Listen, then write the whole sentence your way.",
  }),
];

const L10_PALETTE = [
  rgb(0.12, 0.58, 0.82), rgb(0.53, 0.35, 0.86), rgb(0.1, 0.64, 0.48), rgb(0.94, 0.42, 0.38),
  rgb(0.95, 0.61, 0.12), rgb(0.88, 0.38, 0.65), rgb(0.16, 0.51, 0.88), rgb(0.15, 0.62, 0.36),
  rgb(0.91, 0.47, 0.18), rgb(0.48, 0.38, 0.84), rgb(0.08, 0.59, 0.68),
];

const L10_ACTIVE = [
  game("pickThenWrite", "Pick It, Then Write It", "Pick a picture, hear the sentence, then write the missing word.", "pickThenWrite", L10_PALETTE[0], "touch", {
    intro: "Choose a friendly picture for each sentence. Hear the whole sentence, then write the missing word.",
  }),
  game("frameFade", "Frame Fade", "Write with a sentence frame as it gently fades away.", "frameFade", L10_PALETTE[1], "trace", {
    intro: "The sentence frame will fade a little each time. On the last page, write the whole sentence from memory.",
  }),
  game("todaysSentence", "Today's Sentence", "Answer one friendly question in a complete sentence.", "todaysSentence", L10_PALETTE[2], "trace", {
    intro: "Listen to today's question. Write one sentence with a capital, finger spaces, and an end mark.",
  }),
  game("capitalAndStop", "Capital & Stop", "Find the capital and end mark in every sentence.", "capitalAndStop", L10_PALETTE[3], "search", {
    intro: "Help each sentence you chose stand up. Add its capital start and its stopping mark.",
  }),
  game("oneMore", "One More", "Answer Then what? with a second sentence.", "oneMore", L10_PALETTE[4], "trace", {
    intro: "Read the first sentence. Then answer, Then what, with one sentence of your own.",
  }),
  game("threeWays", "Three Ways", "Grow one idea with two different joining words.", "threeWays", L10_PALETTE[5], "words", {
    intro: "Choose a joining word and finish the idea. Then choose a different joining word and make a new ending.",
  }),
  game("joinSentences", "Join the Sentences", "Join three pairs and hear each new sentence.", "joinSentences", L10_PALETTE[6], "words", {
    intro: "Two short sentences can join into one smooth sentence. Join all three pairs.",
  }),
  game("whoAndThat", "Who & That", "Join two pairs with who or that.", "whoAndThat", L10_PALETTE[7], "words", {
    intro: "Use who for a person or animal and that for a thing. Join both sentence pairs.",
  }),
  game("untangle", "Untangle", "Split one run-on into two sentences that stand up.", "untangle", L10_PALETTE[8], "search", {
    intro: "This run-on has two ideas. Split it into two complete sentences.",
  }),
  game("pictureSentence", "Picture Sentence", "Draw a picture, write one sentence, and save it to My Book.", "pictureSentence", L10_PALETTE[9], "trace", {
    intro: "Make your own drawing, write one sentence under it, and save the page to My Book.",
  }),
  game("wordSprint", "Word Sprint", "Write picture words for one friendly minute.", "wordSprint", L10_PALETTE[10], "trace", {
    intro: "Look at the friendly pictures and write as many words as you can in one minute. Careful words matter more than speed.",
    seconds: 60,
  }),
];

const L11_PALETTE = [
  rgb(0.13, 0.56, 0.82), rgb(0.54, 0.35, 0.84), rgb(0.94, 0.42, 0.42),
  rgb(0.12, 0.63, 0.45), rgb(0.96, 0.61, 0.14), rgb(0.87, 0.4, 0.66),
];

const l11 = (id, title, instruction, engine, symbol, intro) => {
  const index = [
    "partWords", "findThePart", "whoSentence", "whereSentence", "whenSentence", "orderIt",
    "whatHappensSentence", "wantsAndBut", "thenAndSo", "endingsTwoWays", "feelingSentence", "addToMyStory",
  ].indexOf(id);
  return game(id, title, instruction, engine, L11_PALETTE[index % L11_PALETTE.length], symbol, { intro });
};

const L11_ACTIVE = [
  l11("partWords", "Part Words", "Write four useful words for a story part.", "partWords", "words", "A story has useful part words. Hear each gentle word stretched, then write it."),
  l11("findThePart", "Find the Part", "Hear two tiny stories and find the requested part.", "findThePart", "search", "Listen to each tiny story. Tap the sentence that tells the glowing story part."),
  l11("whoSentence", "Who Sentence", "Write who the story follows, then grow the sentence with who.", "sequentialWrite", "words", "Write who the story follows, then grow the sentence with who."),
  l11("whereSentence", "Where Sentence", "Write a sentence that tells where the story happens.", "sequentialWrite", "words", "Tell where Max was. Write the whole sentence and hear it read back."),
  l11("whenSentence", "When Sentence", "Write a sentence that tells when the story happens.", "sequentialWrite", "words", "Tell when the story begins. Write the whole sentence and hear it read back."),
  l11("orderIt", "Order It", "Put three moments in order and label first, then, last.", "orderIt", "sort", "Put the three moments in story order. Then write first, then, and last."),
  l11("whatHappensSentence", "What-Happens Sentence", "Write what happens, then grow it with and.", "sequentialWrite", "words", "Write what Max found. Then grow the action with and."),
  l11("wantsAndBut", "Wants and But", "Write what the character wants and the gentle problem.", "sequentialWrite", "words", "Pip will model a gentle problem sentence. Then write Max's want and but problem."),
  l11("thenAndSo", "Then and So", "Write two sentences that show the response and result.", "sequentialWrite", "words", "Write what happens next with then, and the result with so."),
  l11("endingsTwoWays", "Endings Two Ways", "Write two possible endings and choose the best fit.", "endingsTwoWays", "words", "Write two kind endings. Choose the ending that fits your story best."),
  l11("feelingSentence", "Feeling Sentence", "Write how the character feels and why.", "sequentialWrite", "words", "Tell how Max feels and why using because."),
  l11("addToMyStory", "Add to My Story", "Save seven story parts, then hear the whole story.", "addToMyStory", "check", "Save each line on the seven-part meter. When every part glows, hear your complete story."),
];

const L12_PALETTE = [
  rgb(0.12, 0.57, 0.82), rgb(0.5, 0.34, 0.88), rgb(0.1, 0.64, 0.49), rgb(0.94, 0.48, 0.2),
  rgb(0.91, 0.38, 0.65), rgb(0.17, 0.51, 0.88), rgb(0.96, 0.61, 0.12), rgb(0.16, 0.62, 0.43),
  rgb(0.88, 0.39, 0.48), rgb(0.55, 0.35, 0.84), rgb(0.08, 0.57, 0.66),
];

const L12_ACTIVE = [
  game("meetPOW", "Meet POW", "Put Pick, Organize, and Write in order twice.", "meetPOW", L12_PALETTE[0], "sort", {
    intro: "Meet POW. Pick my idea, Organize my notes, then Write and say more. Put the three steps in order twice.",
  }),
  game("partDetective", "Part Detective", "Find all seven story parts in a short story.", "partDetective", L12_PALETTE[1], "search", {
    intro: "Listen to the story. Find who, when, where, what is wanted, what happens, the ending, and the feeling.",
  }),
  game("partsSort", "Parts Sort", "Sort each story sentence into its part.", "partsSort", L12_PALETTE[2], "sort", {
    intro: "Every story sentence has a job. Place each sentence with its matching story part.",
  }),
  game("pandaPlansOutLoud", "Panda Plans Out Loud", "Listen as Pip plans, gets stuck, and finds a new ending.", "pandaPlansOutLoud", L12_PALETTE[3], "listen", {
    intro: "Pip will plan a story out loud. Listen for how Pip pauses when stuck and uses notes to find an ending.",
  }),
  game("myOrganizer", "My Organizer", "Fill at least five boxes with words or short phrases.", "myOrganizer", L12_PALETTE[4], "grid", {
    intro: "Choose words or short phrases for at least five story parts. These are notes, not full sentences yet.",
  }),
  game("setMyGoal", "Set My Goal", "Choose five parts for a first try or all seven parts.", "setMyGoal", L12_PALETTE[5], "grid", {
    intro: "Choose a story-parts goal. Five is a friendly first try. Seven uses every part.",
  }),
  game("draftWithMeter", "Draft With the Meter", "Write at least six sentences and mark each story part you use.", "draftWithMeter", L12_PALETTE[6], "grid", {
    intro: "Use your organizer to write at least six sentences. Mark a story part each time it lands in your draft.",
  }),
  game("threeQuestions", "Three Questions", "Check your parts, sentence marks, and one grown sentence.", "threeQuestions", L12_PALETTE[7], "check", {
    intro: "Ask three questions about your draft. Notice what you want to improve, then make one change yourself.",
  }),
  game("growOneSentence", "Grow One Sentence", "Pick one sentence and write a bigger version.", "growOneSentence", L12_PALETTE[8], "trace", {
    intro: "Choose and, but, because, or so. Rewrite one sentence with more meaning.",
  }),
  game("intoMyBook", "Into My Book", "Save your finished story to your local My Book.", "intoMyBook", L12_PALETTE[9], "words", {
    intro: "Your story stays in My Book on this device. Listen once more, then save it.",
  }),
  game("readerReply", "Reader Reply", "Share on this device with family, then write one reply sentence.", "readerReply", L12_PALETTE[10], "listen", {
    intro: "Invite a trusted grown-up to read your story on this device. Then write one sentence back to their question.",
  }),
];

const stop = ({
  townId,
  townNumber,
  title,
  subtitle,
  lessonNumber,
  content,
  spellingMode,
  difficulty,
  activities,
}) => ({
  townId,
  townNumber,
  title,
  subtitle,
  lessonNumber,
  courseKey: `writing.lesson${lessonNumber}`,
  content,
  spellingMode,
  difficulty,
  activities,
});

export const WRITING_TOWN_LESSONS = [
  stop({
    townId: "line-lane",
    townNumber: 1,
    title: "Round & Line Lane",
    subtitle: "Lowercase curves, circles, and lines",
    lessonNumber: 2,
    content: "coadgqlit",
    spellingMode: "letterByLetter",
    difficulty: "easy",
    activities: L2_ACTIVE,
  }),
  stop({
    townId: "curve-corner",
    townNumber: 2,
    title: "Bump & Tail Boulevard",
    subtitle: "Lowercase r n m h b p u j y",
    lessonNumber: 3,
    content: "rnmhbpujy",
    spellingMode: "letterByLetter",
    difficulty: "medium",
    activities: L3_ACTIVE,
  }),
  stop({
    townId: "dot-park",
    townNumber: 3,
    title: "Slant & Odd Square",
    subtitle: "Lowercase v w x z k e f s",
    lessonNumber: 4,
    content: "vwxzkefs",
    spellingMode: "letterByLetter",
    difficulty: "medium",
    activities: L4_ACTIVE,
  }),
  stop({
    townId: "letter-market",
    townNumber: 4,
    title: "Straight-Line Studio",
    subtitle: "Your capital and straight-line capitals",
    lessonNumber: 5,
    content: "LTIHEFAKMNVWXYZ",
    spellingMode: "letterByLetter",
    difficulty: "medium",
    activities: L5_ACTIVE,
  }),
  stop({
    townId: "sunny-cafe",
    townNumber: 5,
    title: "Capital Corner",
    subtitle: "Curved capitals and where big letters belong",
    lessonNumber: 6,
    content: "COQGSUJBDPR",
    spellingMode: "letterByLetter",
    difficulty: "medium",
    activities: L6_ACTIVE,
  }),
  stop({
    townId: "pet-shop",
    townNumber: 6,
    title: "Name Neighborhood",
    subtitle: "Your name and Pixley character names",
    lessonNumber: 7,
    content: "Zippy Pip Benny",
    spellingMode: "wordByWord",
    difficulty: "medium",
    activities: L7_ACTIVE,
  }),
  stop({
    townId: "garden-signs",
    townNumber: 7,
    title: "Number Garden",
    subtitle: "Numerals 0–9, counting, and number words",
    lessonNumber: 8,
    content: "0123456789",
    spellingMode: "letterByLetter",
    difficulty: "medium",
    activities: L8_ACTIVE,
  }),
  stop({
    townId: "story-street",
    townNumber: 8,
    title: "Sound Story Street",
    subtitle: "Spell by listening, then write sentences",
    lessonNumber: 9,
    content: "I see a cat",
    spellingMode: "wordByWord",
    difficulty: "medium",
    activities: L9_ACTIVE,
  }),
  stop({
    townId: "town-hall",
    townNumber: 9,
    title: "Sentence Studio",
    subtitle: "Build, grow, join, and revise sentences",
    lessonNumber: 10,
    content: "I can run.",
    spellingMode: "wordByWord",
    difficulty: "hard",
    activities: L10_ACTIVE,
  }),
  stop({
    townId: "my-story-workshop",
    townNumber: 10,
    title: "My Story Workshop",
    subtitle: "Build a seven-part story one sentence at a time",
    lessonNumber: 11,
    content: "Max is a big dog.",
    spellingMode: "wordByWord",
    difficulty: "hard",
    activities: L11_ACTIVE,
  }),
  stop({
    townId: "pow-author-studio",
    townNumber: 11,
    title: "POW Author Studio",
    subtitle: "Plan, draft, revise, and save a brand-new story",
    lessonNumber: 12,
    content: "Pick Organize Write",
    spellingMode: "wordByWord",
    difficulty: "hard",
    activities: L12_ACTIVE,
  }),
];

export const WRITING_TOWN_ID_SET = new Set(WRITING_TOWN_IDS);

export const WRITING_LESSON_NUMBERS = WRITING_TOWN_LESSONS.map((l) => l.lessonNumber);

export function courseKeyForWritingLesson(lessonNumber) {
  return `writing.lesson${Number(lessonNumber)}`;
}

export function writingTownById(townId) {
  return WRITING_TOWN_LESSONS.find((l) => l.townId === townId) || null;
}

export function writingTownByLessonNumber(lessonNumber) {
  return WRITING_TOWN_LESSONS.find((l) => l.lessonNumber === Number(lessonNumber)) || null;
}

export function writingActivityById(townOrLesson, activityId) {
  const lesson =
    typeof townOrLesson === "string"
      ? writingTownById(townOrLesson) || writingTownByLessonNumber(townOrLesson)
      : townOrLesson;
  if (!lesson) return null;
  return lesson.activities.find((a) => a.id === activityId) || null;
}

export const WRITING_ACTIVITY_IDS_BY_LESSON = Object.fromEntries(
  WRITING_TOWN_LESSONS.map((lesson) => [
    lesson.lessonNumber,
    new Set(lesson.activities.map((a) => a.id)),
  ]),
);

export const ALL_WRITING_ACTIVITY_IDS = new Set(
  WRITING_TOWN_LESSONS.flatMap((lesson) =>
    lesson.activities.map((a) => `${lesson.lessonNumber}:${a.id}`),
  ),
);

export const WRITING_HORIZONTAL_FRACTIONS = [
  0.52, 0.35, 0.68, 0.39, 0.68, 0.31, 0.68, 0.39, 0.68, 0.31, 0.67,
];

export function writingTownNodeSize(width) {
  return Math.min(Math.max(width * 0.18, 72), 112);
}

export function writingTownVerticalStep(nodeSize) {
  return Math.max(nodeSize * 1.72, 148);
}
